/* global axios */
import ApiClient from '../ApiClient';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get({
    inboxId,
    status,
    assigneeType,
    page,
    labels,
    teamId,
    conversationType,
    sortBy,
    updatedWithin,
    tickets,
    resolution,
    includeAiHandling,
  }) {
    return axios.get(this.url, {
      params: {
        inbox_id: inboxId,
        team_id: teamId,
        status,
        assignee_type: assigneeType,
        page,
        labels,
        conversation_type: conversationType,
        sort_by: sortBy,
        updated_within: updatedWithin,
        // The tickets lens, tri-state: `true` returns only stamped tickets,
        // narrowed to the disposition the active view asks for, `false` is the
        // chat inbox asking for everything except tickets, and undefined leaves
        // the server scope alone.
        tickets,
        resolution,
        // The open work queue keeps AI-handled (pending) conversations visible
        // alongside open ones; they also stay in the AI Agent "Handling" view.
        include_ai_handling: includeAiHandling,
      },
    });
  }

  filter(payload) {
    return axios.post(`${this.url}/filter`, payload.queryData, {
      params: {
        page: payload.page,
      },
    });
  }

  search({ q }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
      },
    });
  }

  toggleStatus({ conversationId, status, snoozedUntil = null }) {
    return axios.post(`${this.url}/${conversationId}/toggle_status`, {
      status,
      snoozed_until: snoozedUntil,
    });
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  toggleResolution({ conversationId, resolution }) {
    return axios.post(`${this.url}/${conversationId}/toggle_resolution`, {
      resolution,
    });
  }

  createLinkedTicket({
    conversationId,
    inboxId,
    subject,
    priority,
    assigneeId,
    labels,
    internalNote,
    includeTranscript,
  }) {
    return axios.post(`${this.url}/${conversationId}/create_linked_ticket`, {
      inbox_id: inboxId,
      subject,
      priority,
      assignee_id: assigneeId,
      label_list: labels,
      internal_note: internalNote,
      include_transcript: includeTranscript,
    });
  }

  // Tracker linking. `ticketId` is always the tracker ticket; the endpoint's
  // index answers for both roles (a ticket's linked conversations AND a chat's
  // tracker tickets), so `getTicketLinks` takes whichever side is on screen.
  getTicketLinks(conversationId) {
    return axios.get(`${this.url}/${conversationId}/ticket_links`);
  }

  createTicketLink({ ticketId, linkedConversationId }) {
    return axios.post(`${this.url}/${ticketId}/ticket_links`, {
      linked_conversation_id: linkedConversationId,
    });
  }

  deleteTicketLink({ ticketId, linkId }) {
    return axios.delete(`${this.url}/${ticketId}/ticket_links/${linkId}`);
  }

  // The inbox side of the ticket engine bridge (SupportCase domain). A separate
  // resource behind the `support_cases` flag — the tracker links above are a
  // different feature with a flag of their own, and neither knows about the
  // other. The conversation is addressed by its display id, which is what the
  // payloads the dashboard already holds call `id`.
  getSupportCaseLinks(conversationDisplayId) {
    return axios.get(`${this.url}/${conversationDisplayId}/support_case_links`);
  }

  // Two shapes, one endpoint: `caseTypeId` escalates the conversation into a
  // new case, `supportCaseId` attaches an existing one. The server owns the
  // role either way — an escalation becomes the case's `primary_customer` link
  // and an attachment becomes `related` — so nothing here sends one.
  createSupportCaseLink(
    conversationDisplayId,
    {
      caseTypeId = null,
      supportCaseId = null,
      title = null,
      priority = null,
      description = null,
      requesterContactId = null,
      customFields = null,
    } = {}
  ) {
    return axios.post(
      `${this.url}/${conversationDisplayId}/support_case_links`,
      {
        ...(caseTypeId ? { case_type_id: caseTypeId } : {}),
        ...(supportCaseId ? { support_case_id: supportCaseId } : {}),
        ...(title ? { title } : {}),
        ...(priority ? { priority } : {}),
        ...(description ? { description } : {}),
        ...(requesterContactId
          ? { requester_contact_id: requesterContactId }
          : {}),
        // Already keyed by the definition's own `key`, which is a plain
        // identifier — there is no casing to convert on the way out.
        ...(customFields ? { custom_fields: customFields } : {}),
      }
    );
  }

  // The tracker picker in the Details sidebar: every tracker ticket that is
  // not shelved, newest first.
  getTrackerTickets() {
    return axios.get(this.url, {
      params: {
        tickets: true,
        ticket_type: 'tracker',
        status: 'all',
        assignee_type: 'all',
        sort_by: 'latest',
      },
    });
  }

  assignAgent({ conversationId, agentId, assigneeType }) {
    return axios.post(`${this.url}/${conversationId}/assignments`, {
      assignee_id: agentId,
      assignee_type: assigneeType,
    });
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  meta({
    inboxId,
    status,
    assigneeType,
    labels,
    teamId,
    conversationType,
    tickets,
    includeAiHandling,
  }) {
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        labels,
        team_id: teamId,
        conversation_type: conversationType,
        // Same lens as the list request, so the tab counts match the list.
        tickets,
        include_ai_handling: includeAiHandling,
      },
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }

  getInboxAssistant(conversationId) {
    return axios.get(`${this.url}/${conversationId}/inbox_assistant`);
  }

  delete(conversationId) {
    return axios.delete(`${this.url}/${conversationId}`);
  }
}

export default new ConversationApi();
